/**
 * The per-conversation overflow menu, and the confirmation behind Delete.
 *
 * Delete is the only irreversible thing in this panel and it sits one click
 * from Open, so it asks first and names what it is about to remove — a list
 * of similar titles is exactly where the wrong row gets clicked.
 */

import React, { useEffect, useRef, useState } from "react";
import { FiMoreHorizontal } from "react-icons/fi";
import InfoIcon from "@assets/icons/common/information.svg?react";

const ConversationMenu = ({ conversation, onRename, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [draft, setDraft] = useState(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocumentClick = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setIsOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const label = conversation.title || "New conversation";
  const isRenaming = draft !== null;

  // Focused and selected on open, so the first keystroke replaces the old
  // title — which is what someone renaming almost always wants.
  useEffect(() => {
    if (isRenaming) inputRef.current?.select();
  }, [isRenaming]);

  const saveRename = () => {
    const next = (draft ?? "").trim();
    setDraft(null);
    if (next && next !== conversation.title) onRename(next);
  };

  const run = (event, action) => {
    event.stopPropagation();
    setIsOpen(false);
    action();
  };

  return (
    <span className="lou-history__menu" ref={wrapperRef}>
      <button
        type="button"
        className={`lou-history__menu-trigger${isOpen ? " is-open" : ""}`}
        aria-label={`Actions for ${label}`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((open) => !open);
        }}
      >
        <FiMoreHorizontal size={16} />
      </button>

      {isOpen && (
        <span className="lou-history__menu-list" role="menu">
          {/* No "Open" here: the row itself is a button that opens the
              conversation, so a menu item would be a second way to do the
              same thing, one click further away. */}
          <button
            type="button"
            role="menuitem"
            className="lou-history__menu-item"
            onClick={(event) => run(event, () => setDraft(conversation.title || ""))}
          >
            Rename
          </button>
          <button
            type="button"
            role="menuitem"
            className="lou-history__menu-item lou-history__menu-item--danger"
            onClick={(event) => run(event, () => setIsConfirming(true))}
          >
            Delete
          </button>
        </span>
      )}

      {isRenaming && (
        <div
          className="lou-history__confirm-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Rename conversation"
          onClick={(event) => {
            event.stopPropagation();
            setDraft(null);
          }}
        >
          {/* The same card as the delete dialog — a rename is an ordinary
              edit, so it takes the neutral heading and the primary button
              rather than the warning tint. */}
          <div
            className="lou-history__confirm"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="lou-history__confirm-body lou-history__confirm-body--form">
              <label className="lou-history__field">
                <span className="lou-history__field-label">Conversation name</span>
                <input
                  ref={inputRef}
                  className="lou-history__field-input"
                  value={draft}
                  autoFocus
                  maxLength={120}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    event.stopPropagation();
                    if (event.key === "Enter") saveRename();
                    if (event.key === "Escape") setDraft(null);
                  }}
                />
              </label>
            </div>

            <div className="lou-history__confirm-actions">
              <button
                type="button"
                className="lou-history__confirm-cancel"
                onClick={(event) => {
                  event.stopPropagation();
                  setDraft(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="lou-history__confirm-save"
                disabled={!draft.trim()}
                onClick={(event) => {
                  event.stopPropagation();
                  saveRename();
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {isConfirming && (
        <div
          className="lou-history__confirm-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Delete conversation"
          onClick={(event) => {
            event.stopPropagation();
            setIsConfirming(false);
          }}
        >
          {/* Shaped like the product's own delete confirmation — the tinted
              icon, the name carried inside the question, and the footer ruled
              off above two equal buttons. A destructive dialog is the worst
              place to look unfamiliar. */}
          <div
            className="lou-history__confirm"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="lou-history__confirm-body">
              <span className="lou-history__confirm-icon">
                <InfoIcon height={22} />
              </span>

              <p className="lou-history__confirm-title">
                <span>Delete</span>
                <strong>{label}</strong>
                <span>?</span>
              </p>
              <p className="lou-history__confirm-note">
                Are you sure you want to delete this conversation?
              </p>
            </div>

            <div className="lou-history__confirm-actions">
              <button
                type="button"
                className="lou-history__confirm-cancel"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsConfirming(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="lou-history__confirm-delete"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsConfirming(false);
                  onDelete();
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
};

export default ConversationMenu;
